import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { BoxPrimitive } from '#pplx/frontend/aether/primitives/BoxPrimitive/index.js';
import { CommandPanelHeader } from '#pplx/frontend/composer-sdk/components/internal/CommandPanelHeader/index.js';
import { panelScrollHandler } from '#pplx/frontend/composer-sdk/components/internal/panelScroll.js';
import { ComposerContainerContext, COVERED_CHIN_HEIGHT_PROPERTY, } from '#pplx/frontend/composer-sdk/ComposerContainerContext.js';
import { composerShadowGeometry } from '#pplx/frontend/composer-sdk/internal/shadowGeometry.js';
import classNames from 'classnames';
import React from 'react';
function getPanelCornerClassNames(placement, hasTokenPerimeter, hasContent) {
    // Paint only the free edge; the attached edge shares the composer's divider.
    if (placement === 'bottom') {
        return classNames('rounded-b-2xl border-b', hasTokenPerimeter
            ? 'border-b-transparent forced-colors:border-b-[CanvasText]'
            : 'border-b-focus', hasContent && 'before:top-0 before:border-t-[length:var(--shadow-hairline-width)]');
    }
    return classNames('rounded-t-2xl border-t', hasTokenPerimeter
        ? 'border-t-transparent forced-colors:border-t-[CanvasText]'
        : 'border-t-focus', hasContent && 'before:bottom-0 before:border-b-[length:var(--shadow-hairline-width)]');
}
function getSuggestDropdownPanelClassNames({ hasContent, placement, isDashedStroke, composerAppearance, hasInputBorder, isFrameAttached, }) {
    const hasTokenPerimeter = hasContent && !isDashedStroke && !hasInputBorder;
    const panelShadow = hasContent &&
        classNames(
        // Focus stays in the input while suggestions are open, including across portals.
        // Paint the focus treatment above the top-placement background mask.
        // Align the overlay with the outer edge of the retained layout borders.
        hasTokenPerimeter && 'after:-left-px after:-right-px', hasTokenPerimeter &&
            (composerAppearance !== undefined || isFrameAttached) && [
            'after:pointer-events-none after:absolute after:z-[2] after:rounded-inherit after:content-[""] forced-colors:after:hidden',
            // The list continues the frame's perimeter rather than outlining another input.
            isFrameAttached && 'after:shadow-subtle',
            !isFrameAttached &&
                (composerAppearance === 'framed-accent'
                    ? 'after:shadow-composer-accent-focus'
                    : 'after:shadow-composer-focus'),
        ], 
        // Extend the shadow past the join, then clip it back to the panel. This
        // removes the inset highlight as well as the outer shadow at the divider.
        hasTokenPerimeter &&
            (placement === 'bottom'
                ? classNames(composerShadowGeometry.extendTop, 'after:-bottom-px')
                : classNames(composerShadowGeometry.extendBottom, 'after:-top-px')), placement === 'bottom'
            ? composerShadowGeometry.clipTop
            : classNames('shadow-[0_-30px_30px_var(--bg-base)]', composerShadowGeometry.clipBottom));
    // Retain border widths for alignment and semantic dashed outlines while the
    // token supplies the decorative perimeter.
    const panelCorners = getPanelCornerClassNames(placement, hasTokenPerimeter, hasContent);
    // Anchor and isolate the Box shadow overlay so it stacks with this panel.
    const panelPosition = 'relative isolate';
    // The header and body own horizontal padding so the scrollbar sits at the panel edge.
    const panelSurface = `py-3 [background:var(--composer-frame-suggestion-background,var(--bg-raised))] dark:[background:var(--composer-frame-suggestion-background,var(--composer-suggestion-background,var(--bg-raised)))]`;
    const panelSideBorders = classNames('border-x', hasTokenPerimeter
        ? 'border-x-transparent forced-colors:border-x-[CanvasText]'
        : 'border-x-focus');
    const panelStroke = isDashedStroke && 'border-dashed before:border-dashed';
    // Span transparent layout borders so the divider meets the token keyline.
    const panelDivider = hasContent &&
        classNames('before:absolute before:content-[""]', 'before:border-soft', hasTokenPerimeter ? 'before:-inset-x-px' : 'before:inset-x-0');
    // Rise over a chin the panel covers. Panels portaled into the frame inherit the height;
    // others receive it from the container context.
    const panelOffset = placement === 'bottom' && 'mt-[calc(-1*var(--composer-covered-chin-height,0px))]';
    return classNames(composerShadowGeometry.overhang, panelOffset, panelPosition, panelSurface, panelSideBorders, panelStroke, panelDivider, panelCorners, panelShadow);
}
function getPanelShadowProps(hasContent, isDashedStroke) {
    if (!hasContent || isDashedStroke)
        return {};
    return { shadowSize: 'md', shadowFocused: true, shadowLayer: 'overlay' };
}
export const SuggestDropdownPanel = React.memo(function SuggestDropdownPanel({ children, composerAppearance: composerAppearanceProp, placement = 'bottom', hasContent = true, isDashedStroke = false, maxBodyHeightPx, bodyRef, onReachEnd, title, subtitle, leadingAccessory, trailingAccessory, className, }) {
    const { suggestionAppearance, isSuggestionAttachedToFrame = false, inputAppearance, hasTokenShadow, decorativeGlow, coveredChinHeightPx = 0, } = React.useContext(ComposerContainerContext);
    const composerAppearance = composerAppearanceProp ?? suggestionAppearance;
    const isFrameAttached = isSuggestionAttachedToFrame && !isDashedStroke;
    // A plain raised input draws a CSS focus border, so the panel draws the same border.
    const hasInputBorder = composerAppearance === undefined &&
        inputAppearance === 'raised' &&
        !hasTokenShadow &&
        !decorativeGlow;
    return (_jsxs(BoxPrimitive, { noBorder: true, ...getPanelShadowProps(hasContent && !composerAppearance && !hasInputBorder && !isFrameAttached, isDashedStroke), className: classNames(getSuggestDropdownPanelClassNames({
            hasContent,
            placement,
            isDashedStroke,
            composerAppearance,
            hasInputBorder,
            isFrameAttached,
        }), className), style: placement === 'bottom' && coveredChinHeightPx > 0
            ? { [COVERED_CHIN_HEIGHT_PROPERTY]: `${coveredChinHeightPx}px` }
            : undefined, children: [_jsx("div", { className: "px-3", children: _jsx(CommandPanelHeader, { title: title, subtitle: subtitle, leadingAccessory: leadingAccessory, trailingAccessory: trailingAccessory }) }), _jsx("div", { ref: bodyRef, onScroll: panelScrollHandler(onReachEnd), 
                // Flush to the panel edges (`px-3` matches the frame's former horizontal padding), so
                // the scrollbar sits against the panel edge with the rows kept inset.
                className: classNames('px-3', maxBodyHeightPx != null && 'overflow-y-auto'), style: maxBodyHeightPx != null ? { maxHeight: maxBodyHeightPx } : undefined, children: children })] }));
});
//# sourceMappingURL=index.js.map